import { CustomForm } from '../types/CustomForm'
import { AnswerState } from './dynamicFormReducer'
import {
  PLAYER_INFO_SECTION,
  PLAYER_INFO_FIELDS,
  ReservedField,
} from '../constants/prefillMappings'

export interface RosterPayload {
  name_first?: string
  name_last?: string
  email?: string
  birth_date?: string
  player_type_id?: string
  preferred_jersey_number?: string
  other_membership_number?: string
}

// Helper to determine if a field is required (handles 1/0, true/false, etc.)
function isFieldReserved(is_reserved: any): boolean {
  // Accepts 1, true, '1', 'true' as required
  return (
    is_reserved === 1 ||
    is_reserved === true ||
    is_reserved === '1' ||
    is_reserved === 'true'
  )
}

/**
 * 🔄 mapReservedAnswersToRoster: Maps ID-based answers to roster payload fields
 *
 * This function reverses the logic used in usePrefillReservedFields to map
 * form answers (keyed by item.id) back to the canonical field names needed
 * for roster API updates. It finds reserved fields in the Player Info section
 * and maps their answers to the appropriate roster payload properties.
 *
 * @param form - The form definition containing groups and items
 * @param answers - Form answers keyed by item.id (e.g., {"ztGDxvjIueSboCe7": "felipe"})
 * @returns Object with roster payload fields, omitting empty values
 */
export const mapReservedAnswersToRoster = (
  form: CustomForm,
  answers: AnswerState
): RosterPayload => {
  console.log('🔄 mapReservedAnswersToRoster: Starting mapping', {
    formGroups: form?.groups?.length || 0,
    answersCount: Object.keys(answers).length,
  })

  const result: RosterPayload = {}

  // Early return if no form definition
  if (!form?.groups) {
    console.log(
      '🔄 mapReservedAnswersToRoster: No form definition, returning empty'
    )
    return result
  }

  // Find the Player Info reserved section
  const playerInfoSection = form.groups.find(
    (group) =>
      isFieldReserved(group.is_reserved) && group.name === PLAYER_INFO_SECTION
  )

  if (!playerInfoSection) {
    console.log(
      '🔄 mapReservedAnswersToRoster: Player Info section not found',
      {
        availableSections: form.groups.map((g) => ({
          name: g.name,
          is_reserved: g.is_reserved,
        })),
      }
    )
    return result
  }

  console.log('🔄 mapReservedAnswersToRoster: Found Player Info section', {
    sectionName: playerInfoSection.name,
    itemCount: playerInfoSection.items.length,
  })

  // Process each field mapping
  PLAYER_INFO_FIELDS.forEach((mapping: ReservedField) => {
    // Find the corresponding form item
    const formItem = playerInfoSection.items.find(
      (item) =>
        isFieldReserved(item.is_reserved) && item.name === mapping.fieldName
    )

    if (!formItem) {
      console.log('🔄 mapReservedAnswersToRoster: Form item not found', {
        fieldName: mapping.fieldName,
        availableItems: playerInfoSection.items.map((i) => ({
          name: i.name,
          is_reserved: i.is_reserved,
        })),
      })
      return
    }

    // Get the answer value using the form item's ID
    const answerValue = answers[formItem.id]

    if (
      answerValue !== undefined &&
      answerValue !== null &&
      answerValue !== ''
    ) {
      let processedValue = String(answerValue).trim()

      // Special handling for birth_date fields - ensure YYYY-MM-DD format
      if (mapping.playerKey === 'birth_date' && processedValue.includes('T')) {
        // Convert ISO date to YYYY-MM-DD format
        processedValue = processedValue.split('T')[0]
        console.log('🔄 mapReservedAnswersToRoster: Converted ISO date', {
          original: answerValue,
          converted: processedValue,
        })
      }

      console.log('🔄 mapReservedAnswersToRoster: Mapping field', {
        fieldName: mapping.fieldName,
        playerKey: mapping.playerKey,
        itemId: formItem.id,
        value: processedValue,
      })

      // Assign to result using the playerKey
      ;(result as any)[mapping.playerKey] = processedValue
    } else {
      console.log('🔄 mapReservedAnswersToRoster: No value found for field', {
        fieldName: mapping.fieldName,
        playerKey: mapping.playerKey,
        itemId: formItem.id,
        answerValue,
      })
    }
  })

  console.log('🔄 mapReservedAnswersToRoster: Final result', {
    mappedFields: Object.keys(result).length,
    result,
  })

  return result
}
